import { DataProvider } from "@/api/DataProvider";
import { AppState, CountryCodeResponse, MessageType } from "@/interfaces/IApp";
import { ThemeType } from "@nexus/shared-ui";
import { message as antdMessage } from "antd";
import type { StateCreator } from "zustand";
import type { AppStore } from "../appStore";

export interface AppSlice extends AppState {
  setChangeLanguage: (language: AppState["language"]) => void;
  setUserInfo: (userInfo: AppState["userInfo"]) => void;
  setUserProfile: (userProfile: AppState["userProfile"]) => void;
  setBreadcrumb: (breadcrumb: AppState["breadcrumb"]) => void;
  setModuleName: (moduleName: AppState["moduleName"]) => void;
  setMessage: (message: MessageType) => void;
  fetchCountryCode: () => Promise<void>;
  fetchNationality: () => Promise<void>;
  setTheme: (theme: ThemeType) => void;
}

const initialState: AppState = {
  language: "fa-IR",
  theme: "light",
  moduleName: null,
  userInfo: {},
  userProfile: {},
  breadcrumb: [],
  countryCodes: {
    loading: false,
    error: null,
    data: [],
  },
  nationality: {
    error: null,
    loading: false,
    data: [],
  },
  message: {
    type: null,
    text: null,
  },
};

const getErrorMessage = (error: any) =>
  error?.response?.data?.message || error?.message || "Error";

export const createAppSlice: StateCreator<AppStore, [], [], AppSlice> = (
  set
) => ({
  ...initialState,

  setChangeLanguage: (language) => {
    set({ language });
  },

  setUserInfo: (userInfo) => {
    set({ userInfo });
  },

  setUserProfile: (userProfile) => {
    set({ userProfile });
  },

  setBreadcrumb: (breadcrumb) => {
    set({ breadcrumb });
  },

  setModuleName: (moduleName) => {
    set({ moduleName });
  },

  setMessage: (newMessage) => {
    set({
      message: {
        id: Date.now(),
        ...newMessage,
      },
    });
  },

  fetchCountryCode: async () => {
    set((state) => ({
      countryCodes: {
        ...state.countryCodes,
        loading: true,
        error: null,
      },
    }));

    try {
      const result = await DataProvider.getList("v1/countries/country-codes");

      const data = result.map((item: CountryCodeResponse) => ({
        value: item.countryCode,
        label: item.countryCode,
      }));

      set({
        countryCodes: {
          loading: false,
          error: null,
          data,
        },
      });
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      antdMessage.error(errorMessage);

      set((state) => ({
        countryCodes: {
          ...state.countryCodes,
          loading: false,
          error: errorMessage,
        },
      }));
    }
  },

  fetchNationality: async () => {
    set((state) => ({
      nationality: {
        ...state.nationality,
        loading: true,
        error: null,
      },
    }));

    try {
      const result = await DataProvider.getList("v1/nationalities");

      set({
        nationality: {
          loading: false,
          error: null,
          data: result,
        },
      });
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      antdMessage.error(errorMessage);

      set((state) => ({
        nationality: {
          ...state.nationality,
          loading: false,
          error: errorMessage,
        },
      }));
    }
  },
  setTheme: (theme) => {
    set({ theme });
  },
});
